import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchKYCStatus, submitKYC, updateDocument, setBiometricsCaptured } from '../../store/slices/kycSlice';
import { updateProfile } from '../../store/slices/authSlice';
import { addToast } from '../../store/slices/uiSlice';
import DocumentUpload from './DocumentUpload';
import BiometricCapture from './BiometricCapture';
import KYCStatusBadge from './KYCStatusBadge';
import Button from '../UI/Button';
import FormSection from '../UI/FormSection';

export type KYCStep = 'intro' | 'documents' | 'biometrics' | 'review' | 'submitted';

interface KYCFlowProps {
  onComplete?: () => void;
}

const KYCFlow: React.FC<KYCFlowProps> = ({ onComplete }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { status, documents, hasBiometrics, isSubmitting, isLoading, expired, expirationDate } = useAppSelector((state) => state.kyc);
  
  const [currentStep, setCurrentStep] = useState<KYCStep>('intro');
  const [selectedDocumentType, setSelectedDocumentType] = useState<'passport' | 'drivers_license' | 'national_id' | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Set<KYCStep>>(new Set());

  useEffect(() => {
    dispatch(fetchKYCStatus());
  }, [dispatch]);

  useEffect(() => {
    // Determine current step based on status and progress
    // If expired, reset to intro
    if (expired) {
      setCurrentStep('intro');
    } else if (status === 'approved' || status === 'pending' || status === 'rejected') {
      setCurrentStep('submitted');
    } else if (hasBiometrics && documents.some(d => {
      const isPassport = d.type === 'passport';
      return isPassport ? d.frontImage : (d.frontImage && d.backImage);
    })) {
      setCurrentStep('review');
    } else if (hasBiometrics) {
      setCurrentStep('documents');
    } else if (documents.some(d => {
      const isPassport = d.type === 'passport';
      return isPassport ? d.frontImage : (d.frontImage && d.backImage);
    })) {
      setCurrentStep('biometrics');
    } else if (documents.length > 0) {
      setCurrentStep('documents');
    }
  }, [status, documents, hasBiometrics, expired]);

  const handleDocumentUpload = (type: 'passport' | 'drivers_license' | 'national_id', side: 'front' | 'back', url: string) => {
    dispatch(updateDocument({ 
      type, 
      side, 
      url
    }));
    dispatch(updateProfile({}));
    
    // Check if document is complete
    const currentDoc = documents.find(d => d.type === type) || { frontImage: '', backImage: '' };
    const isPassport = type === 'passport';
    // For passport, only front is needed. For others, both sides are needed.
    const isComplete = isPassport 
      ? (side === 'front' ? url : currentDoc.frontImage)
      : (side === 'front' ? url : currentDoc.frontImage) && (side === 'back' ? url : currentDoc.backImage);
    
    if (isComplete) {
      setCompletedSteps(prev => new Set([...prev, 'documents']));
      // Auto-advance if biometrics are done, otherwise go to biometrics
      if (hasBiometrics) {
        setTimeout(() => setCurrentStep('review'), 1000);
      } else {
        setTimeout(() => setCurrentStep('biometrics'), 1000);
      }
    }
  };

  const handleBiometricCapture = () => {
    dispatch(setBiometricsCaptured());
    dispatch(updateProfile({}));
    setCompletedSteps(prev => new Set([...prev, 'biometrics']));
    // Auto-advance to review
    setTimeout(() => setCurrentStep('review'), 1000);
  };

  const handleSubmit = async () => {
    try {
      await dispatch(submitKYC()).unwrap();
      dispatch(addToast({ message: 'KYC submitted successfully. It will be reviewed shortly.', type: 'success' }));
      setCurrentStep('submitted');
      setCompletedSteps(prev => new Set([...prev, 'review']));
      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      dispatch(addToast({
        message: error.message || 'Failed to submit KYC',
        type: 'error',
      }));
    }
  };

  const canProceedToReview = () => {
    const hasCompleteDocument = documents.some(d => d.frontImage && d.backImage);
    return hasCompleteDocument && hasBiometrics;
  };

  const steps: Array<{ id: KYCStep; title: string; description: string }> = [
    { id: 'intro', title: 'Introduction', description: 'Learn about KYC verification' },
    { id: 'documents', title: 'Upload Documents', description: 'Upload your identity documents' },
    { id: 'biometrics', title: 'Biometric Capture', description: 'Capture your biometric data' },
    { id: 'review', title: 'Review & Submit', description: 'Review your information and submit' },
  ];

  const getStepIndex = (step: KYCStep) => {
    return steps.findIndex(s => s.id === step);
  };

  const isStepComplete = (step: KYCStep) => {
    if (step === 'intro') return true;
    if (step === 'documents') return documents.some(d => d.frontImage && d.backImage);
    if (step === 'biometrics') return hasBiometrics;
    if (step === 'review') return status === 'pending' || status === 'approved';
    return false;
  };

  const isStepAccessible = (step: KYCStep) => {
    const stepIndex = getStepIndex(step);
    const currentIndex = getStepIndex(currentStep);
    if (stepIndex <= currentIndex) return true;
    if (step === 'review' && canProceedToReview()) return true;
    return false;
  };

  // Don't show flow if already submitted/approved/rejected (unless expired)
  if ((status === 'approved' || status === 'pending' || status === 'rejected') && !expired) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <KYCStatusBadge 
            status={status} 
            citizenStatus={user?.kyc?.citizenStatus}
            expirationDate={expirationDate}
            expired={expired}
            className="text-lg px-4 py-2" 
          />
          {status === 'pending' && (
            <p className="mt-4 text-[var(--color-subtext-color)]">
              Your KYC submission is under review. You will be notified once the review is complete.
            </p>
          )}
          {status === 'approved' && !expired && (
            <div className="mt-4 space-y-2">
              <p className="text-[var(--color-success-700)]">
                ✓ Your KYC has been approved! You are now a verified citizen.
              </p>
              {expirationDate && (
                <p className="text-sm text-[var(--color-subtext-color)]">
                  Expires: {new Date(expirationDate).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
          {status === 'rejected' && user?.kyc?.verificationData?.rejectionReason && (
            <div className="mt-4 p-4 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg max-w-2xl mx-auto">
              <h3 className="font-semibold text-[var(--color-error-800)] mb-2">Rejection Reason</h3>
              <p className="text-sm text-[var(--color-error-700)]">{user.kyc.verificationData.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show expired message if KYC is expired
  if (expired) {
    return (
      <div className="space-y-6">
        <div className="p-6 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg">
          <div className="text-center">
            <KYCStatusBadge 
              status={status} 
              citizenStatus={user?.kyc?.citizenStatus}
              expirationDate={expirationDate}
              expired={expired}
              className="text-lg px-4 py-2 mb-4" 
            />
            <h2 className="text-xl font-semibold text-[var(--color-error-800)] mb-2">
              KYC Verification Expired
            </h2>
            <p className="text-[var(--color-error-700)] mb-4">
              Your KYC verification has expired. Please complete the verification process again to maintain your citizen status.
            </p>
            {expirationDate && (
              <p className="text-sm text-[var(--color-error-600)]">
                Expired on: {new Date(expirationDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="bg-[var(--color-default-background)] p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => {
            const isActive = currentStep === step.id;
            const isComplete = isStepComplete(step.id);
            const isAccessible = isStepAccessible(step.id);
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => isAccessible && setCurrentStep(step.id)}
                    disabled={!isAccessible}
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm transition-all
                      ${isActive
                        ? 'bg-[var(--color-brand-primary)] text-white ring-4 ring-[var(--color-brand-100)]'
                        : isComplete
                        ? 'bg-[var(--color-success-500)] text-white'
                        : isAccessible
                        ? 'bg-[var(--color-neutral-200)] text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-300)]'
                        : 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-400)] cursor-not-allowed'
                      }
                    `}
                  >
                    {isComplete && !isActive ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </button>
                  <div className="mt-2 text-center">
                    <p className={`text-xs font-medium ${isActive ? 'text-[var(--color-brand-primary)]' : 'text-[var(--color-subtext-color)]'}`}>
                      {step.title}
                    </p>
                    <p className="text-[10px] text-[var(--color-subtext-color)] mt-1 hidden md:block">
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`
                    flex-1 h-1 mx-2 transition-colors
                    ${isComplete || (index < getStepIndex(currentStep))
                      ? 'bg-[var(--color-success-500)]'
                      : 'bg-[var(--color-neutral-200)]'
                    }
                  `} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-[var(--color-default-background)] p-6 rounded-lg shadow-sm">
        {currentStep === 'intro' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[var(--color-default-font)] mb-4">
                KYC Verification Process
              </h2>
              <p className="text-[var(--color-subtext-color)] mb-6">
                To become a verified citizen and unlock full platform features, please complete the KYC (Know Your Customer) verification process.
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-[var(--color-brand-50)] border border-[var(--color-brand-200)] rounded-lg">
                <h3 className="font-semibold text-[var(--color-brand-800)] mb-2 flex items-center gap-2">
                  <span>📋</span> Step 1: Upload Documents
                </h3>
                <p className="text-sm text-[var(--color-brand-700)]">
                  Upload clear images of your identity document (passport, driver's license, or national ID). Both front and back sides are required.
                </p>
              </div>

              <div className="p-4 bg-[var(--color-brand-50)] border border-[var(--color-brand-200)] rounded-lg">
                <h3 className="font-semibold text-[var(--color-brand-800)] mb-2 flex items-center gap-2">
                  <span>🔒</span> Step 2: Biometric Capture
                </h3>
                <p className="text-sm text-[var(--color-brand-700)]">
                  Capture your biometric data using your device's camera. This information is securely encrypted and used for identity verification only.
                </p>
              </div>

              <div className="p-4 bg-[var(--color-brand-50)] border border-[var(--color-brand-200)] rounded-lg">
                <h3 className="font-semibold text-[var(--color-brand-800)] mb-2 flex items-center gap-2">
                  <span>✓</span> Step 3: Review & Submit
                </h3>
                <p className="text-sm text-[var(--color-brand-700)]">
                  Review all your information and submit for verification. Our team will review your submission within 1-3 business days.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--color-neutral-border)]">
              <Button onClick={() => setCurrentStep('documents')} className="w-full md:w-auto">
                Get Started
              </Button>
            </div>
          </div>
        )}

        {currentStep === 'documents' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[var(--color-default-font)] mb-2">
                Upload Identity Documents
              </h2>
              <p className="text-[var(--color-subtext-color)]">
                Please upload clear images of your identity document. You can choose from passport, driver's license, or national ID.
              </p>
            </div>

            {!selectedDocumentType ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['passport', 'drivers_license', 'national_id'] as const).map((type) => {
                  const doc = documents.find(d => d.type === type);
                  const isPassport = type === 'passport';
                  const isComplete = isPassport ? doc?.frontImage : (doc?.frontImage && doc?.backImage);
                  
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedDocumentType(type)}
                      className={`
                        p-6 border-2 rounded-lg text-left transition-all
                        ${isComplete
                          ? 'border-[var(--color-success-500)] bg-[var(--color-success-50)]'
                          : 'border-[var(--color-neutral-border)] hover:border-[var(--color-brand-primary)] hover:bg-[var(--color-brand-50)]'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-[var(--color-default-font)] capitalize">
                          {type.replace('_', ' ')}
                        </h3>
                        {isComplete && (
                          <svg className="w-5 h-5 text-[var(--color-success-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm text-[var(--color-subtext-color)]">
                        {isComplete ? 'Document uploaded' : 'Click to upload'}
                      </p>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[var(--color-default-font)] capitalize">
                    {selectedDocumentType.replace('_', ' ')}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDocumentType(null)}
                  >
                    Choose Different Document
                  </Button>
                </div>
                <DocumentUpload
                  type={selectedDocumentType}
                  frontImage={documents.find(d => d.type === selectedDocumentType)?.frontImage}
                  backImage={documents.find(d => d.type === selectedDocumentType)?.backImage}
                  onUploadComplete={(side, url) => handleDocumentUpload(selectedDocumentType, side, url)}
                />
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-[var(--color-neutral-border)]">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('intro')}
              >
                Back
              </Button>
              {documents.some(d => {
                const isPassport = d.type === 'passport';
                return isPassport ? d.frontImage : (d.frontImage && d.backImage);
              }) && (
                <Button
                  onClick={() => setCurrentStep('biometrics')}
                >
                  Continue to Biometrics
                </Button>
              )}
            </div>
          </div>
        )}

        {currentStep === 'biometrics' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[var(--color-default-font)] mb-2">
                Biometric Verification
              </h2>
              <p className="text-[var(--color-subtext-color)]">
                Capture your biometric data for additional security verification. This information is securely encrypted.
              </p>
            </div>

            <BiometricCapture
              onCaptureComplete={handleBiometricCapture}
            />

            <div className="flex justify-between pt-4 border-t border-[var(--color-neutral-border)]">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('documents')}
              >
                Back
              </Button>
              {hasBiometrics && (
                <Button
                  onClick={() => setCurrentStep('review')}
                >
                  Continue to Review
                </Button>
              )}
            </div>
          </div>
        )}

        {currentStep === 'review' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[var(--color-default-font)] mb-2">
                Review Your Information
              </h2>
              <p className="text-[var(--color-subtext-color)]">
                Please review all your information before submitting for verification.
              </p>
            </div>

            {/* Documents Summary */}
            <FormSection title="Uploaded Documents">
              <div className="space-y-3">
                {documents.map((doc, index) => {
                  const isPassport = doc.type === 'passport';
                  const isComplete = isPassport ? doc.frontImage : (doc.frontImage && doc.backImage);
                  return (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg ${
                        isComplete
                          ? 'border-[var(--color-success-200)] bg-[var(--color-success-50)]'
                          : 'border-[var(--color-warning-200)] bg-[var(--color-warning-50)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[var(--color-default-font)] capitalize">
                            {doc.type.replace('_', ' ')}
                          </p>
                          {doc.documentId && (
                            <p className="text-sm text-[var(--color-subtext-color)] mt-1">
                              ID: {doc.documentId}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isComplete ? (
                            <span className="text-sm text-[var(--color-success-700)] flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Complete
                            </span>
                          ) : (
                            <span className="text-sm text-[var(--color-warning-700)]">
                              Incomplete
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </FormSection>

            {/* Biometrics Summary */}
            <FormSection title="Biometric Data">
              <div className={`p-4 border rounded-lg ${
                hasBiometrics
                  ? 'border-[var(--color-success-200)] bg-[var(--color-success-50)]'
                  : 'border-[var(--color-warning-200)] bg-[var(--color-warning-50)]'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-[var(--color-default-font)]">
                    Biometric Information
                  </p>
                  {hasBiometrics ? (
                    <span className="text-sm text-[var(--color-success-700)] flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Captured
                    </span>
                  ) : (
                    <span className="text-sm text-[var(--color-warning-700)]">
                      Not Captured
                    </span>
                  )}
                </div>
              </div>
            </FormSection>

            <div className="p-4 bg-[var(--color-neutral-50)] border border-[var(--color-neutral-border)] rounded-lg">
              <p className="text-sm text-[var(--color-subtext-color)]">
                By submitting, you confirm that all information provided is accurate and belongs to you. 
                Your submission will be reviewed within 1-3 business days.
              </p>
            </div>

            <div className="flex justify-between pt-4 border-t border-[var(--color-neutral-border)]">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('biometrics')}
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canProceedToReview() || isSubmitting}
                isLoading={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCFlow;

